import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

import Particle from "../Particle";
import Testimonials from "./Testimonials";
import reviews from "./data";
import useInViewOnce from "../../hooks/useInViewOnce";

import codesoilHero from "../../Assets/codesoil.jpg";

import codesoil1 from "../../Assets/code_soil1.jpg";
import codesoilplants1 from "../../Assets/code_soil_plants.jpeg";
import codesoil2 from "../../Assets/code_soil2.jpg";
import codesoil6 from "../../Assets/code_soil6.jpg";
import codesoilplants2 from "../../Assets/code_soil_plants2.jpeg";
import codesoil7 from "../../Assets/code_soil7.jpg";
import codesoilplants3 from "../../Assets/code_soil_plants3.jpeg";
import codesoil8 from "../../Assets/code_soil8.jpg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import SlickLazyImage from "../shared/SlickLazyImage";
import { useTranslation } from "react-i18next";

const sliderImages = [
  { src: codesoil1, altKey: "programs.codeAndSoil.sliderAlts.0" },
  { src: codesoilplants1, altKey: "programs.codeAndSoil.sliderAlts.1" },
  { src: codesoil2, altKey: "programs.codeAndSoil.sliderAlts.2" },
  { src: codesoil6, altKey: "programs.codeAndSoil.sliderAlts.3" },
  { src: codesoilplants2, altKey: "programs.codeAndSoil.sliderAlts.4" },
  { src: codesoil7, altKey: "programs.codeAndSoil.sliderAlts.5" },
  { src: codesoilplants3, altKey: "programs.codeAndSoil.sliderAlts.6" },
  { src: codesoil8, altKey: "programs.codeAndSoil.sliderAlts.7" },
];

function Neighborhood() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeSlide, setActiveSlide] = React.useState(0);

  const handleRegister = () => {
    navigate("/registration#codesoil-section");
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3600,
    pauseOnHover: true,
    pauseOnFocus: true,
    adaptiveHeight: false,
    lazyLoad: "progressive",
    beforeChange: (_current, next) => setActiveSlide(next),
  };

  const videoId = "5mPU-mIQgoQ";

  const { ref: topRef, hasEntered: topSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: overviewRef, hasEntered: overviewSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: whatRef, hasEntered: whatSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: outcomesRef, hasEntered: outcomesSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: beyondRef, hasEntered: beyondSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: testimonialsRef, hasEntered: testimonialsSeen } = useInViewOnce({ threshold: 0.18, once: true });

  const whatWeDo = t("programs.codeAndSoil.whatWeDo", { returnObjects: true });
  const outcomes = t("programs.codeAndSoil.outcomes", { returnObjects: true });
  const snapshotBullets = t("programs.codeAndSoil.snapshot.bullets", { returnObjects: true });

  return (
    <Container fluid className="about-section codesoil-page">
      <Particle />

      <Container className="codesoil-container">
        <header className="codesoil-hero">
          <h1 className="codesoil-hero-title">
            {t("programs.codeAndSoil.hero.titleLeft")} <span className="purple">&amp;</span> {t("programs.codeAndSoil.hero.titleRight")}
          </h1>
          <p className="codesoil-hero-lead">{t("programs.codeAndSoil.hero.lead")}</p>
          <div className="codesoil-hero-cta">
            <Button onClick={handleRegister} className="custom-yellow-btn codesoil-cta-btn">
              {t("common.registerNow")}
            </Button>
          </div>
        </header>

        <div ref={topRef} className={`codesoil-reveal${topSeen ? " is-seen" : ""}`}>
          <Row className="codesoil-top" style={{ justifyContent: "center" }}>
            <Col md={7} className="codesoil-left">
              <div className="policy-card ambassadors-slider-card" aria-label={t("programs.codeAndSoil.aria.highlights")}>
                <Slider {...settings}>
                  {sliderImages.map((image, index) => (
                    <div key={index} className="ambassadors-slide">
                      <SlickLazyImage
                        src={image.src}
                        alt={t(image.altKey)}
                        className="ambassadors-slide-img"
                        index={index}
                        activeIndex={activeSlide}
                        total={sliderImages.length}
                        buffer={2}
                        eager
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </Col>

            <Col md={5} className="codesoil-right">
              <div className="policy-card codesoil-summary-card">
                <h2 className="codesoil-card-title">{t("programs.codeAndSoil.snapshot.title")}</h2>
                <p className="about-text codesoil-card-text">
                  {t("programs.codeAndSoil.snapshot.text")}
                </p>
                <ul className="codesoil-bullets">
                  {Array.isArray(snapshotBullets)
                    ? snapshotBullets.map((item) => <li key={item}>{item}</li>)
                    : null}
                </ul>
                <Button onClick={handleRegister} className="custom-yellow-btn codesoil-cta-btn codesoil-cta-btn-secondary">
                  {t("common.registerHere")}
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        <section
          ref={overviewRef}
          className={`codesoil-section codesoil-reveal${overviewSeen ? " is-seen" : ""}`}
          aria-label={t("programs.codeAndSoil.aria.overview")}
        >
          <Row className="codesoil-overview-row" style={{ justifyContent: "center" }}>
            <Col md={7} className="codesoil-overview-left">
              <div className="policy-card codesoil-copy-card">
                <h2 className="codesoil-section-heading">{t("programs.codeAndSoil.overview.title")}</h2>
                <p className="codesoil-body">
                  {t("programs.codeAndSoil.overview.p1Start")}{" "}
                  <strong className="purple">{t("programs.codeAndSoil.overview.p1Emphasis1")}</strong>{" "}
                  {t("programs.codeAndSoil.overview.p1Middle")}{" "}
                  <strong className="purple">{t("programs.codeAndSoil.overview.p1Emphasis2")}</strong>
                  {t("programs.codeAndSoil.overview.p1End")}
                </p>
                <p className="codesoil-body">
                  {t("programs.codeAndSoil.overview.p2")}
                </p>
                <p className="codesoil-body">
                  {t("programs.codeAndSoil.overview.p3")}
                </p>
              </div>
            </Col>

            <Col md={5} className="codesoil-overview-right">
              <div
                className="policy-card codesoil-image-card codesoil-image-card--side"
                aria-label={t("programs.codeAndSoil.aria.programImage")}
              >
                <Tilt glareEnable={false}>
                  <img
                    src={codesoilHero}
                    className="img-fluid codesoil-image"
                    alt={t("programs.codeAndSoil.images.programAlt")}
                    loading="lazy"
                    decoding="async"
                  />
                </Tilt>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={whatRef}
          className={`codesoil-section codesoil-reveal${whatSeen ? " is-seen" : ""}`}
          aria-label={t("programs.codeAndSoil.aria.whatWeDo")}
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">{t("programs.codeAndSoil.whatWeDoTitle")}</h2>
          <div className="codesoil-grid">
            {Array.isArray(whatWeDo)
              ? whatWeDo.map((item) => (
              <div key={item.title} className="policy-card codesoil-feature-card">
                <h3 className="codesoil-feature-title">{item.title}</h3>
                <p className="codesoil-feature-text">{item.text}</p>
              </div>
                ))
              : null}
          </div>
        </section>

        <section
          ref={outcomesRef}
          className={`codesoil-section codesoil-reveal${outcomesSeen ? " is-seen" : ""}`}
          aria-label={t("programs.codeAndSoil.aria.outcomes")}
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">{t("programs.codeAndSoil.outcomesTitle")}</h2>
          <div className="codesoil-grid">
            {Array.isArray(outcomes)
              ? outcomes.map((item) => (
              <div key={item.title} className="policy-card codesoil-feature-card">
                <h3 className="codesoil-feature-title">{item.title}</h3>
                <p className="codesoil-feature-text">{item.text}</p>
              </div>
                ))
              : null}
          </div>
        </section>

        <section
          ref={beyondRef}
          className={`codesoil-section codesoil-reveal${beyondSeen ? " is-seen" : ""}`}
          aria-label={t("programs.codeAndSoil.aria.beyond")}
        >
          <Row>
            <Col md={12}>
              <div className="policy-card codesoil-copy-card">
                <h2 className="codesoil-section-heading">{t("programs.codeAndSoil.beyond.title")}</h2>
                <p className="codesoil-body">
                  {t("programs.codeAndSoil.beyond.p1")}
                </p>
                <p className="codesoil-body">
                  {t("programs.codeAndSoil.beyond.p2")}
                </p>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={videoRef}
          className={`codesoil-section codesoil-reveal${videoSeen ? " is-seen" : ""}`}
          aria-label={t("programs.codeAndSoil.aria.video")}
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">{t("programs.codeAndSoil.videoTitle")}</h2>
          <div className="policy-card codesoil-video-card">
            <div className="codesoil-video">
              <iframe
                title={t("programs.codeAndSoil.videoIframeTitle")}
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section
          ref={testimonialsRef}
          className={`codesoil-testimonials-section codesoil-reveal${testimonialsSeen ? " is-seen" : ""}`}
          aria-label={t("programs.codeAndSoil.aria.testimonials")}
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">{t("programs.codeAndSoil.testimonialsTitle")}</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>
      </Container>
    </Container>
  );
}

export default Neighborhood;


